/**
 * Shared value formatting for the Google Analytics dashboard.
 *
 * The backend tags every KPI with a `format` so the same tile component can render a count, a rate
 * and a duration without knowing which metric it is showing.
 */

const numberFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

/** Format a raw count (1234 → "1,234"). */
export const formatNumber = (value) => numberFormatter.format(Math.round(value ?? 0));

/** Format a GA4 rate, which arrives as a 0–1 fraction (0.4213 → "42.1%"). */
export const formatPercent = (value) => `${((value ?? 0) * 100).toFixed(1)}%`;

/** Format a duration in seconds: "Ns" under a minute (e.g. 45 → "45s"), else "Mm SSs". */
export const formatDuration = (seconds) => {
  const total = Math.round(seconds ?? 0);
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  return `${minutes}m ${String(total % 60).padStart(2, "0")}s`;
};

/** Format a value according to the backend-supplied KPI format tag. */
export const formatByType = (value, format) => {
  if (format === "percent") return formatPercent(value);
  if (format === "duration") return formatDuration(value);
  return formatNumber(value);
};

/** Shorten a long URL path for a table cell without hiding which page it is. */
export const truncatePath = (path, max = 48) =>
  !path || path.length <= max ? path || "(not set)" : `${path.slice(0, max - 1)}…`;
